import { useQuery, useReactiveVar } from "@apollo/client";
import { setNotification, userContextData } from "helpers/cache";
import {
  CRMSETTINGS_QUOTE_SHIPPED_EMAIL_TPLS,
  DOMAINS_QUERY,
} from "modules/AdminDashboard/Services/Queries/Queries";
import { FC, ReactNode, useMemo } from "react";
import CRMSetting from "types/CRMsetting";
import {
  CLIENT_ACCESS_LEVEL_QUERY,
  COUNTRIES_QUERY,
  EXPENSES_QUERY,
  GENDERS_QUERY,
  INDUSTRY_TAGS_QUERY,
  LEAD_SOURCE_QUERY,
  PAYMENT_STATUSSES_QUERY,
  PAYMENT_TYPES_QUERY,
  QUOTE_IMAGE_TYPES_QUERY,
  QUOTE_LEAD_STATUSES_QUERY,
  QUOTE_STATUSES_QUERY,
  SHIPPIND_ADDRESS_TYPES_QUERY,
  SHIPPING_METHODS_QUERY,
  SUPPLIERS_QUERY,
  USERS_QUERY,
} from "../../../modules/Services/Queries/Common/CommonQueries";
import { AddressType } from "../../../types/AddressType";
import { ClientAccessLevel } from "../../../types/ClientAccessLevel";
import { Country } from "../../../types/Country";
import { Expense } from "../../../types/Expense";
import {
  FormSelectGroupOption,
  FormSelectOptionItem,
  FormSelectValue,
} from "../../../types/Form";
import { Gender } from "../../../types/Gender";
import { ImageType } from "../../../types/ImageType";
import { IndustryTag } from "../../../types/IndustryTag";
import { LeadSource } from "../../../types/LeadSource";
import { LeadStatus } from "../../../types/LeadStatus";
import { PaymentStatus } from "../../../types/PaymentStatus";
import { PaymentType } from "../../../types/PaymentType";
import { QuoteStatus } from "../../../types/QuoteStatus";
import { ShippingMethod } from "../../../types/ShippingMethod";
import { User } from "../../../types/User";
import Select from "./Select";
import Domain from "types/Domain";

const SELECTABLE_QUOTE_DOMAINS = [
  "usbmemorydirect.com",
  "everypromo.com",
  "cross-sell",
];

interface SelectOptionsFromServer {
  users: User[];
  countries: Country[];
  genders: Gender[];
  clientAccessLevels: ClientAccessLevel[];
  industryTags: IndustryTag[];
  shippingAddressTypes: AddressType[];
  leadSource: LeadSource[];
  configQuotesLeadStatuses: LeadStatus[];
  configQuotesStatuses: QuoteStatus[];
  shippingMethods: ShippingMethod[];
  paymentTypes: PaymentType[];
  suppliers: User[];
  paymentStatuses: PaymentStatus[];
  expenses: Expense[];
  quoteImageTypes: ImageType[];
  quoteShippedEmailTpl: CRMSetting[];
  domains: Domain[];
}

const SelectFromServerData: FC<{
  isNewQuote?: boolean;
  value: FormSelectValue;
  onChange: (value: FormSelectValue) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  className?: string;
  classNameForSelect?: string;
  isIncorrectValue?: boolean;
  disabled?: boolean;
  labelWidth?: string;
  label?: string | ReactNode;
  type:
    | "users"
    | "countries"
    | "genders"
    | "clientAccessLevels"
    | "industryTags"
    | "shippingAddressTypes"
    | "leadSource"
    | "configQuotesLeadStatuses"
    | "configQuotesStatuses"
    | "shippingMethods"
    | "paymentTypes"
    | "suppliers"
    | "paymentStatuses"
    | "expenses"
    | "quoteImageTypes"
    | "quoteShippedEmailTpl"
    | "domains";
  selectWidth?: string;
  callBack?: (options: FormSelectGroupOption["options"]) => void;
}> = ({
  isNewQuote,
  value,
  onChange,
  onFocus,
  onBlur,
  className,
  classNameForSelect,
  isIncorrectValue,
  disabled,
  labelWidth,
  label,
  type,
  selectWidth,
}) => {
  const queryList = useMemo(
    () => ({
      users: USERS_QUERY,
      countries: COUNTRIES_QUERY,
      genders: GENDERS_QUERY,
      clientAccessLevels: CLIENT_ACCESS_LEVEL_QUERY,
      industryTags: INDUSTRY_TAGS_QUERY,
      shippingAddressTypes: SHIPPIND_ADDRESS_TYPES_QUERY,
      leadSource: LEAD_SOURCE_QUERY,
      configQuotesLeadStatuses: QUOTE_LEAD_STATUSES_QUERY,
      configQuotesStatuses: QUOTE_STATUSES_QUERY,
      shippingMethods: SHIPPING_METHODS_QUERY,
      paymentTypes: PAYMENT_TYPES_QUERY,
      suppliers: SUPPLIERS_QUERY,
      paymentStatuses: PAYMENT_STATUSSES_QUERY,
      expenses: EXPENSES_QUERY,
      quoteImageTypes: QUOTE_IMAGE_TYPES_QUERY,
      quoteShippedEmailTpl: CRMSETTINGS_QUOTE_SHIPPED_EMAIL_TPLS,
      domains: DOMAINS_QUERY,
    }),
    []
  );

  const leadStatusId = Number(value);
  const { data, loading: isLoadingSelectData } = useQuery(queryList[type], {
    variables: type === "configQuotesLeadStatuses" ? {
      selectableOnly: true,
      includeIds: Number.isNaN(leadStatusId) || leadStatusId <= 0 ? [] : [leadStatusId],
    } : undefined,
    onError: (error) => {
      setNotification([{ type: "ERROR", message: error.message }]);
    },
  });

  const userDetails: any = useReactiveVar(userContextData);

  const userId = useMemo(() => userDetails?.user?.id, [userDetails]);
  const roleName = useMemo(
    () => userDetails?.user?.roles[0]?.name?.toLowerCase(),
    [userDetails]
  );

  const selectOptions = useMemo((): FormSelectGroupOption[] => {
      const options = data as SelectOptionsFromServer | undefined;
      if (!options?.[type]) return [];

      if (type === "users") {
        return [
          {
            label: "activeUsers",
            options: options[type]
              .filter((user) => user && !user.isDeleted)
              .map((user) => ({
                id: user.id,
                value: user.userName,
              })),
          },
          {
            label: "deletedUsers",
            options: options[type]
              .filter((user) => user && user.isDeleted)
              .map((user) => ({
                id: user.id,
                value: user.userName,
              })),
          },
        ];

      }

      if (type === "countries") {
        return [
          {
            options: options[type].map((country) => ({
              id: country.id,
              value: country.name,
            })),
          },
        ];

      }

      if (type === "genders") {
        return [
          {
            options: options[type].map((gender) => ({
              id: parseInt(gender.label),
              value: gender.value,
            })),
          },
        ];

      }

      if (type === "quoteShippedEmailTpl") {
        return [
          {
            options: options[type].map(({ id, value }) => ({
              id,
              value,
            })),
          },
        ];

      }

      if (type === "clientAccessLevels") {
        return [
          {
            options: options[type].map((clientAccessLevel) => ({
              id: parseInt(clientAccessLevel.label),
              value: clientAccessLevel.value,
            })),
          },
        ];

      }

      if (type === "industryTags") {
        return [
          {
            options: options[type].map((industryTag) => ({
              id: industryTag.id,
              value: industryTag.name,
            })),
          },
        ];

      }

      if (type === "shippingAddressTypes") {
        return [
          {
            options: options[type].map(
              (shippingAddressType) => shippingAddressType.value
            ),
          },
        ];

      }

      if (type === "leadSource") {
        return [
          {
            options: options[type].map((leadSource) => ({
              id: parseInt(leadSource.label),
              value: leadSource.value,
            })),
          },
        ];

      }
      if (type === "domains") {
        const domainsByUrl = new Map(
          options[type].map((domain) => [domain.url, domain])
        );
        const optionsToAdd: FormSelectOptionItem[] =
          SELECTABLE_QUOTE_DOMAINS.filter((url) => domainsByUrl.has(url)).map(
            (url) => ({
              id: url,
              value: url,
            })
          );

        if (
          typeof value === "string" &&
          value !== "" &&
          !SELECTABLE_QUOTE_DOMAINS.includes(value)
        ) {
          optionsToAdd.push({
            id: value,
            value: `Legacy - ${value}`,
            disabled: true,
          });
        }

        return [
          {
            options: optionsToAdd,
          },
        ];

      }

      if (type === "configQuotesLeadStatuses") {
        const leadStatusOptions = options[type].map((leadStatus) => {
          const isViewOnly =
            leadStatus.isLegacy || leadStatus.isSelectable !== true;

          return {
            id: leadStatus.id,
            value: isViewOnly
              ? `View Only - ${leadStatus.name}`
              : leadStatus.name,
            disabled: isViewOnly,
          };
        });

        return [
          {
            options: leadStatusOptions,
          },
        ];

      }

      if (type === "configQuotesStatuses") {
        return [
          {
            options: options[type]
              .filter((option) => {
                if (
                  value !== option.id &&
                  ((option.position > 3 && roleName === "sales") ||
                    (roleName === "supplier" && option.id !== 6)) &&
                  (isNewQuote || !disabled)
                ) {
                  return false;
                }
                return true;
              })
              .map((paymentStatus) => {
                return {
                  id: paymentStatus.id,
                  value: paymentStatus.name,
                };
              }),
          },
        ];

      }

      if (type === "suppliers") {
        return [
          {
            options: [
              { id: 0, value: "" },
              ...options[type].map((supplier) => ({
                id: supplier.id,
                value: supplier.userName,
              })),
            ],
          },
        ];
      }

      if (type === "paymentTypes") {
        return [
          {
            options: options[type].map((paymentType) => ({
              id: +paymentType.label,
              value: paymentType.value,
            })),
          },
        ];
      }

      if (type === "shippingMethods") {
        return [
          {
            options: options[type].map((shippingMethod) => ({
              id: +shippingMethod.label,
              value: shippingMethod.value,
            })),
          },
        ];
      }

      if (type === "paymentStatuses") {
        return [
          {
            options: options[type].map((paymentStatus) => ({
              id: +paymentStatus.label,
              value: paymentStatus.value,
            })),
          },
        ];
      }

      if (type === "expenses") {
        return [
          {
            options: options[type].map((expense) => ({
              id: expense.id,
              value: expense.name,
            })),
          },
        ];

      }

      if (type === "quoteImageTypes") {
        return [
          {
            options: options[type].map((imageType) => ({
              id: parseInt(imageType.label),
              value: imageType.value,
            })),
          },
        ];

      }
      return [];
    }, [data, type, value, roleName, isNewQuote, disabled]
  );

  const withNullbaleValue = type === "paymentTypes" ||
    type === "shippingMethods" || type === "paymentStatuses";

  const defaultSelectValue = useMemo((): FormSelectValue => {
    // Payment fields can be genuinely unset. Viewing them must not select CC/zero.
    if (withNullbaleValue || disabled) return undefined;
    if (type === "configQuotesLeadStatuses") {
      if (isNewQuote === false) return undefined;
      const firstSelectable = selectOptions[0]?.options.find(
        (option) => typeof option === "object" && !option.disabled
      );
      return typeof firstSelectable === "object" ? firstSelectable.id : undefined;
    }
    if (type === "users") return userId || 0;
    if (type === "domains") return "usbmemorydirect.com";
    if (type === "countries" || type === "expenses") return 1;
    if (type === "shippingAddressTypes") return "Shipping";
    return 0;
  }, [type, userId, disabled, isNewQuote, withNullbaleValue, selectOptions]);

  return (
    <Select
      value={type === "configQuotesLeadStatuses" && isNewQuote !== false && !value
        ? undefined : value}
      defaultValue={defaultSelectValue}
      optionsGroups={selectOptions}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      className={className}
      classNameForSelect={classNameForSelect}
      isIncorrectValue={isIncorrectValue}
      disabled={disabled}
      name={`${type}-select`}
      loading={isLoadingSelectData}
      labelWidth={labelWidth}
      label={label}
      selectWidth={selectWidth}
      withNullbaleValue={withNullbaleValue}
    />
  );
};

export default SelectFromServerData;
